import { Link, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OperatorPicker } from "@/components/wallet/OperatorPicker";
import { PhoneInput } from "@/components/common/PhoneInput";
import { parsePhone, toE164, type Country } from "@/lib/countries";
import { Money } from "@/components/common/Money";
import { useWithdrawInit } from "@/hooks/useWallet";
import { usePendingWithdrawal } from "@/hooks/useTransactions";
import { useAuth } from "@/hooks/useAuth";
import {
  MIN_WITHDRAW,
  MAX_WITHDRAW_PER_TX,
  calculateFee,
  totalWithFee,
  operatorsForCountry,
} from "@/lib/constants";
import { formatFcfa } from "@/lib/format";
import { toast } from "sonner";

const schema = z.object({
  amount: z.coerce.number().int().min(MIN_WITHDRAW).max(MAX_WITHDRAW_PER_TX),
  phone: z.object({
    country: z.object({ code: z.string(), name: z.string(), dial: z.string(), flag: z.string() }),
    local: z.string().min(6).max(15),
  }),
  operator: z.string().min(1, "Choisis un opérateur"),
});
type FormValues = z.infer<typeof schema>;

export function Withdraw() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const withdraw = useWithdrawInit();
  const pending = usePendingWithdrawal();

  const initialPhone = parsePhone(profile?.phone ?? null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { amount: 1000, phone: initialPhone, operator: "" },
  });
  const phone = form.watch("phone");
  const operator = form.watch("operator");
  const amount = form.watch("amount");

  const countryOperators = operatorsForCountry(phone?.country?.code ?? "");
  if (operator && !countryOperators.find((o) => o.id === operator)) {
    form.setValue("operator", "");
  }

  const fee = calculateFee(amount);
  const totalToDebit = totalWithFee(amount);
  const insufficient = (profile?.wallet_balance ?? 0) < totalToDebit;

  async function onSubmit(data: FormValues) {
    if ((profile?.wallet_balance ?? 0) < totalWithFee(data.amount)) {
      toast.error(t("wallet.insufficient"));
      return;
    }
    try {
      const phoneE164 = toE164(data.phone.country as Country, data.phone.local);
      await withdraw.mutateAsync({ amount: data.amount, phone: phoneE164, operator: data.operator });
      toast.success(t("wallet.withdrawPending"));
      navigate("/wallet");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    }
  }

  return (
    <div className="space-y-4 pt-2 pb-2">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon-sm"><Link to="/wallet"><ArrowLeft className="size-[18px]" /></Link></Button>
        <h1 className="text-base font-semibold tracking-tight flex-1 text-center">Retirer</h1>
        <div className="size-8" />
      </header>

      <div className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-card px-4 py-3">
        <span className="text-xs text-muted-foreground">Solde disponible</span>
        <Money amount={profile?.wallet_balance ?? 0} className="font-semibold num" />
      </div>

      {pending.data && (
        <div className="rounded-xl border border-warning/30 bg-warning/[0.06] px-4 py-3">
          <p className="text-xs font-semibold text-warning">Retrait en attente</p>
          <p className="text-2xs text-muted-foreground mt-0.5">
            <Money amount={pending.data.amount} /> en validation admin
          </p>
        </div>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <div className="space-y-2">
          <Label>Montant</Label>
          <Input
            type="number"
            inputMode="numeric"
            min={MIN_WITHDRAW}
            max={profile?.wallet_balance ?? 0}
            {...form.register("amount", { valueAsNumber: true })}
            className="text-2xl font-bold h-14 num"
          />
          <p className="text-2xs text-muted-foreground tabular-nums">
            Min {formatFcfa(MIN_WITHDRAW)} · max {formatFcfa(MAX_WITHDRAW_PER_TX)} par retrait
          </p>
          {insufficient && <p className="text-xs text-destructive">{t("wallet.insufficient")}</p>}
        </div>

        {/* Breakdown frais retrait — clair pour le user */}
        {amount >= MIN_WITHDRAW && (
          <div className="rounded-xl border border-white/[0.06] bg-card p-3 space-y-1.5 text-xs">
            <div className="flex items-center justify-between text-muted-foreground">
              <span>Tu recevras sur ton mobile money</span>
              <span className="font-semibold text-foreground num">{formatFcfa(amount)}</span>
            </div>
            <div className="flex items-center justify-between text-muted-foreground">
              <span>Frais opérateur (4 %)</span>
              <span className="font-medium num">{formatFcfa(fee)}</span>
            </div>
            <div className="h-px bg-white/[0.06] my-1" />
            <div className="flex items-center justify-between">
              <span className="font-semibold">Prélèvement total wallet</span>
              <span className={`text-base font-bold num ${insufficient ? "text-destructive" : "text-primary"}`}>
                {formatFcfa(totalToDebit)}
              </span>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label>Numéro de téléphone</Label>
          <Controller
            control={form.control}
            name="phone"
            render={({ field }) => (
              <PhoneInput
                value={field.value as { country: Country; local: string }}
                onChange={field.onChange}
              />
            )}
          />
          {form.formState.errors.phone?.local && (
            <p className="text-xs text-destructive">{form.formState.errors.phone.local.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Opérateur</Label>
          <OperatorPicker
            countryCode={phone?.country?.code ?? "SN"}
            value={operator || null}
            onChange={(op: string) => form.setValue("operator", op, { shouldValidate: true })}
          />
          {form.formState.errors.operator && <p className="text-xs text-destructive">{form.formState.errors.operator.message}</p>}
        </div>

        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={withdraw.isPending}
          disabled={!operator || insufficient}
        >
          Demander le retrait
        </Button>
      </form>
    </div>
  );
}
